<template>
  <div id="accordionTemplateContacts" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTemplateContacts" class="accordion-header">
        <button
          aria-controls="collapseTemplateContacts"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTemplateContacts"
          data-bs-toggle="collapse"
          type="button">
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="contact" size="xxl" space="p-12"/>
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t('template.contact.contacts') }}</div>
              <div class="fw-light accordion-subtitle">{{ $t('template.contact.contactsSubtitle') }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTemplateContacts"
        aria-labelledby="headingTemplateContacts"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTemplateContacts">
        <div class="accordion-body pb-32 pb-xl-48">
          <form
            id="template-update-contact-text-form"
            class="d-flex flex-column justify-content-between h-100"
          >
            <fieldset>
              <legend class="visually-hidden">{{ $t('template.contact.contactInformation') }}</legend>

              <div class="form-outline">
                <label class="label">
                  <span>{{ $t('template.contact.contactInformation') }}</span>
                </label>

                <v-html-editor
                  id="template-update-contact-text"
                  ref="template-update-contact-text-ref"
                  :value="contactInformationLocal"
                  @input="contactInformationLocal = $event"
                  @media-uploaded="editorMedia.push($event)"
                />
              </div>
            </fieldset>

            <v-button
              :disabled="!meta.valid || disableButton || !meta.dirty"
              :is-loading="accepted"
              class="btn-icon w-fit align-self-end mt-24"
              size="sm"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t('general.button.save') }}
              <v-icon icon="arrow-right" size="sm" space="ms-12"/>
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import { useField } from 'vee-validate';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VHtmlEditor from '@/components/vendor/htmlEditor/VHtmlEditor.vue';
import api from '@/api';
import useFormValidation from '@/helpers/form';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import Template from '@/api/objects/Template';

export default defineComponent({
  name: 'TheTemplateSetupContacts',
  components: {
    VHtmlEditor,
    VIcon,
    VButton,
  },
  emits: ['reloadTemplate'],
  props: {
    agency: {
      type: String,
      required: true,
    },
    template: {
      type: Template,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();

    const rules = yup.object({
      contactInformationLocal: yup.string()
        .nullable()
        .label(t('template.contact.contactInformation')),
    });

    const initialValues = {
      contactInformationLocal: props.template.contactText,
    };

    const form = useFormValidation(rules, initialValues);

    const { value: contactInformationLocal } = useField<string>('contactInformationLocal');

    return {
      ...form,
      contactInformationLocal,
    };
  },
  data() {
    return {
      disableButton: false as boolean,
      editorMedia: [] as any[],
    };
  },
  methods: {
    async updateTemplate() {
      try {
        this.toggleAccepted();
        this.disableButton = true;

        const data = {
          name: this.template.name,
          travel_type: this.template.travelType,
          origin: this.template.originId,
          destination: this.template.destinationId,
          terms_conditions_text: this.template.termsConditionsText,
          complementary_text: this.template.complementaryText,
          contact_text: this.contactInformationLocal,
        };

        const template: Template = await api.template.update(this.agency, this.template.id, data);

        if (!!this.editorMedia && this.editorMedia.length > 0) {
          const editorMedia = {
            media: this.editorMedia,
            model_type: 'template',
            collection: 'tinymce',
            model_id: this.template.id,
          };

          await api.media.confirm('s3', editorMedia);
        }

        this.$toast.success(template.message);

        this.$emit('reloadTemplate');

        this.disableButton = false;
        this.toggleAccepted();
      } catch (e: any) {
        this.disableButton = false;
        this.toggleAccepted();

        console.error(e);

        this.$toast.error(e.response.data.message);
      }
    },
  },
});
</script>
